const statusDe = {
  UNCHECKED: 'Ungeprüft',
  VALID: 'Gültig',
  INVALID: 'Ungültig',
  NOT_CHECKABLE: 'Nicht prüfbar',
  SERVICE_UNAVAILABLE: 'Dienst nicht erreichbar',
} as const;

export const vatValidationDe = {
  title: 'USt-IdNr.-Prüfung',
  subtitle: 'Qualifizierte Bestätigung über den offiziellen EU-VIES-Dienst.',
  help: 'Hilfe zur VIES-Prüfung',
  history: 'Prüfprotokoll',
  source: 'Quelle',
  nameMatch: 'Name',
  addressMatch: 'Anschrift',
  requestIdentifier: 'Anfragekennung',
  returnedName: 'Von VIES gemeldeter Name',
  returnedAddress: 'Von VIES gemeldete Anschrift',
  countryAndNumber: 'Geprüftes Land und Nummer',
  providerDate: 'VIES-Prüfdatum',
  nameAssessment: 'Namensvergleich',
  notProvided: 'Kein Name geliefert',
  rawValueNotice: 'Unveränderter, XML-dekodierter Wert aus der VIES-Antwort.',
  normalizedMasterName: 'Stammdaten – normalisierte Vergleichsansicht',
  normalizedReportedName: 'VIES – normalisierte Vergleichsansicht',
  normalizedNotice:
    'Die Normalisierung dient nur dem Vergleich. Hervorgehobene Bestandteile fehlen in der jeweils anderen Ansicht.',
  disclaimer:
    'Die technische VIES-Prüfung ist ein Nachweis zum Prüfzeitpunkt, aber keine automatische steuerrechtliche Garantie.',
  status: statusDe,
  match: { MATCH: 'Übereinstimmung', NO_MATCH: 'Abweichung', NOT_PROCESSED: 'Nicht verarbeitet' },
  nameState: {
    NOT_APPLICABLE: 'Nicht anwendbar',
    NOT_PROVIDED: 'Name nicht geliefert',
    LIKELY_MATCH: 'Name stimmt wahrscheinlich überein',
    MISMATCH: 'Name weicht ab',
    MANUAL_REVIEW: 'Manuelle Prüfung erforderlich',
  },
  legacy: {
    title: 'Älteres Ergebnis erneut prüfen',
    description:
      'Dieser Nachweis stammt aus dem früheren VIES-Mapping. Er bleibt unverändert erhalten; eine erneute Prüfung erzeugt einen korrekt zugeordneten Nachweis.',
  },
  actions: { validate: 'USt-IdNr. prüfen', revalidate: 'Erneut prüfen' },
  states: {
    loading: 'Prüfprotokoll wird geladen',
    loadError: 'Prüfprotokoll konnte nicht geladen werden',
    executeError: 'Die Prüfung konnte nicht ausgeführt werden.',
    empty: 'Noch keine VIES-Prüfung vorhanden.',
    noVatTitle: 'Keine USt-IdNr. hinterlegt',
    noVat: 'Hinterlegen Sie zuerst eine syntaktisch gültige USt-IdNr. im Stammdatensatz.',
  },
  result: {
    VALID: 'USt-IdNr. als gültig bestätigt.',
    INVALID: 'USt-IdNr. als ungültig bestätigt.',
    NOT_CHECKABLE: 'Die USt-IdNr. konnte nicht geprüft werden.',
    SERVICE_UNAVAILABLE:
      'VIES ist nicht erreichbar; die USt-IdNr. wurde nicht als ungültig markiert.',
  },
  deviation: {
    title: 'Abweichung zu den Stammdaten',
    description: 'Prüfen Sie Name und Anschrift vor der steuerlichen Verwendung.',
  },
  settings: {
    action: 'Einstellungen',
    title: 'Wiederholungsprüfung',
    description: 'Steuert Cache, technische Wiederholung und regelmäßige VIES-Prüfungen.',
    automatic: 'Regelmäßig automatisch prüfen',
    intervalDays: 'Prüfintervall (Tage)',
    cacheHours: 'Cache (Stunden)',
    retryHours: 'Wiederholung bei Ausfall (Stunden)',
    saved: 'USt-ID-Einstellungen gespeichert.',
  },
} as const;

export const vatValidationEn = {
  title: 'VAT ID validation',
  subtitle: 'Qualified confirmation through the official EU VIES service.',
  help: 'Help with VIES validation',
  history: 'Validation history',
  source: 'Source',
  nameMatch: 'Name',
  addressMatch: 'Address',
  requestIdentifier: 'Request identifier',
  returnedName: 'Name returned by VIES',
  returnedAddress: 'Address returned by VIES',
  countryAndNumber: 'Validated country and number',
  providerDate: 'VIES validation date',
  nameAssessment: 'Name comparison',
  notProvided: 'No name provided',
  rawValueNotice: 'Unmodified, XML-decoded value from the VIES response.',
  normalizedMasterName: 'Master data — normalized comparison view',
  normalizedReportedName: 'VIES — normalized comparison view',
  normalizedNotice:
    'Normalization is used only for comparison. Highlighted parts are missing from the other view.',
  disclaimer:
    'The technical VIES check is evidence at the time of validation, but not an automatic tax-law guarantee.',
  status: {
    UNCHECKED: 'Unchecked',
    VALID: 'Valid',
    INVALID: 'Invalid',
    NOT_CHECKABLE: 'Not checkable',
    SERVICE_UNAVAILABLE: 'Service unavailable',
  },
  match: { MATCH: 'Match', NO_MATCH: 'Mismatch', NOT_PROCESSED: 'Not processed' },
  nameState: {
    NOT_APPLICABLE: 'Not applicable',
    NOT_PROVIDED: 'Name not provided',
    LIKELY_MATCH: 'Name probably matches',
    MISMATCH: 'Name differs',
    MANUAL_REVIEW: 'Manual review required',
  },
  legacy: {
    title: 'Validate this older result again',
    description:
      'This evidence uses the previous VIES mapping. It remains unchanged; validating again creates a correctly mapped proof.',
  },
  actions: { validate: 'Validate VAT ID', revalidate: 'Validate again' },
  states: {
    loading: 'Loading validation history',
    loadError: 'Validation history could not be loaded',
    executeError: 'The validation could not be performed.',
    empty: 'No VIES validation has been recorded yet.',
    noVatTitle: 'No VAT ID stored',
    noVat: 'First add a syntactically valid VAT ID to the master record.',
  },
  result: {
    VALID: 'VAT ID confirmed as valid.',
    INVALID: 'VAT ID confirmed as invalid.',
    NOT_CHECKABLE: 'VAT ID could not be checked.',
    SERVICE_UNAVAILABLE: 'VIES is currently unavailable; the VAT ID was not marked invalid.',
  },
  deviation: {
    title: 'Difference from master data',
    description: 'Review the name and address before using the result for tax purposes.',
  },
  settings: {
    action: 'Settings',
    title: 'Recurring validation',
    description: 'Controls caching, technical retries and recurring VIES validations.',
    automatic: 'Validate automatically on schedule',
    intervalDays: 'Validation interval (days)',
    cacheHours: 'Cache (hours)',
    retryHours: 'Retry after outage (hours)',
    saved: 'VAT ID settings saved.',
  },
} as const;

export const vatValidationTr = {
  title: 'KDV numarası doğrulama',
  subtitle: 'Resmî AB VIES hizmeti üzerinden nitelikli doğrulama.',
  help: 'VIES doğrulama yardımı',
  history: 'Doğrulama geçmişi',
  source: 'Kaynak',
  nameMatch: 'Ad',
  addressMatch: 'Adres',
  requestIdentifier: 'İstek kimliği',
  returnedName: 'VIES tarafından bildirilen ad',
  returnedAddress: 'VIES tarafından bildirilen adres',
  countryAndNumber: 'Kontrol edilen ülke ve numara',
  providerDate: 'VIES kontrol tarihi',
  nameAssessment: 'Ad karşılaştırması',
  notProvided: 'Ad bildirilmedi',
  rawValueNotice: 'VIES yanıtındaki değiştirilmemiş, XML kodu çözülmüş değer.',
  normalizedMasterName: 'Ana veri — normalize edilmiş karşılaştırma',
  normalizedReportedName: 'VIES — normalize edilmiş karşılaştırma',
  normalizedNotice:
    'Normalizasyon yalnızca karşılaştırma içindir. Vurgulanan parçalar diğer görünümde yoktur.',
  disclaimer:
    'Teknik VIES kontrolü, kontrol anına ait bir kanıttır; otomatik bir vergi hukuku garantisi değildir.',
  status: {
    UNCHECKED: 'Kontrol edilmedi',
    VALID: 'Geçerli',
    INVALID: 'Geçersiz',
    NOT_CHECKABLE: 'Kontrol edilemiyor',
    SERVICE_UNAVAILABLE: 'Hizmete ulaşılamıyor',
  },
  match: { MATCH: 'Eşleşiyor', NO_MATCH: 'Farklı', NOT_PROCESSED: 'İşlenmedi' },
  nameState: {
    NOT_APPLICABLE: 'Uygulanamaz',
    NOT_PROVIDED: 'Ad bildirilmedi',
    LIKELY_MATCH: 'Ad büyük olasılıkla eşleşiyor',
    MISMATCH: 'Ad farklı',
    MANUAL_REVIEW: 'Elle inceleme gerekli',
  },
  legacy: {
    title: 'Eski sonucu yeniden kontrol edin',
    description:
      'Bu kanıt önceki VIES eşlemesini kullanır. Değiştirilmeden korunur; yeniden kontrol doğru eşlenmiş yeni bir kanıt oluşturur.',
  },
  actions: { validate: 'KDV numarasını kontrol et', revalidate: 'Yeniden kontrol et' },
  states: {
    loading: 'Doğrulama geçmişi yükleniyor',
    loadError: 'Doğrulama geçmişi yüklenemedi',
    executeError: 'Doğrulama yapılamadı.',
    empty: 'Henüz VIES doğrulaması yok.',
    noVatTitle: 'KDV numarası kayıtlı değil',
    noVat: 'Önce ana kayda sözdizimsel olarak geçerli bir KDV numarası ekleyin.',
  },
  result: {
    VALID: 'KDV numarası geçerli olarak doğrulandı.',
    INVALID: 'KDV numarası geçersiz olarak doğrulandı.',
    NOT_CHECKABLE: 'KDV numarası kontrol edilemedi.',
    SERVICE_UNAVAILABLE: 'VIES kullanılamıyor; numara geçersiz olarak işaretlenmedi.',
  },
  deviation: {
    title: 'Ana verilerle fark',
    description: 'Vergisel kullanımdan önce adı ve adresi kontrol edin.',
  },
  settings: {
    action: 'Ayarlar',
    title: 'Düzenli doğrulama',
    description: 'Önbelleği, teknik tekrarları ve düzenli VIES kontrollerini yönetir.',
    automatic: 'Düzenli olarak otomatik kontrol et',
    intervalDays: 'Kontrol aralığı (gün)',
    cacheHours: 'Önbellek (saat)',
    retryHours: 'Kesintiden sonra tekrar (saat)',
    saved: 'KDV numarası ayarları kaydedildi.',
  },
} as const;

export const vatValidationFr = {
  title: 'Validation du numéro de TVA',
  subtitle: 'Confirmation qualifiée via le service officiel VIES de l’UE.',
  help: 'Aide sur la validation VIES',
  history: 'Historique des validations',
  source: 'Source',
  nameMatch: 'Nom',
  addressMatch: 'Adresse',
  requestIdentifier: 'Identifiant de demande',
  returnedName: 'Nom renvoyé par VIES',
  returnedAddress: 'Adresse renvoyée par VIES',
  countryAndNumber: 'Pays et numéro contrôlés',
  providerDate: 'Date du contrôle VIES',
  nameAssessment: 'Comparaison du nom',
  notProvided: 'Aucun nom fourni',
  rawValueNotice: 'Valeur inchangée, après décodage XML, issue de la réponse VIES.',
  normalizedMasterName: 'Données de base — vue normalisée',
  normalizedReportedName: 'VIES — vue normalisée',
  normalizedNotice:
    'La normalisation sert uniquement à la comparaison. Les éléments surlignés manquent dans l’autre vue.',
  disclaimer:
    'Le contrôle technique VIES constitue une preuve à la date du contrôle, mais pas une garantie fiscale automatique.',
  status: {
    UNCHECKED: 'Non contrôlé',
    VALID: 'Valide',
    INVALID: 'Invalide',
    NOT_CHECKABLE: 'Non vérifiable',
    SERVICE_UNAVAILABLE: 'Service indisponible',
  },
  match: { MATCH: 'Concordance', NO_MATCH: 'Écart', NOT_PROCESSED: 'Non traité' },
  nameState: {
    NOT_APPLICABLE: 'Sans objet',
    NOT_PROVIDED: 'Nom non fourni',
    LIKELY_MATCH: 'Le nom correspond probablement',
    MISMATCH: 'Le nom diffère',
    MANUAL_REVIEW: 'Contrôle manuel requis',
  },
  legacy: {
    title: 'Contrôler à nouveau cet ancien résultat',
    description:
      'Cette preuve utilise l’ancien mapping VIES. Elle reste inchangée ; un nouveau contrôle crée une preuve correctement attribuée.',
  },
  actions: { validate: 'Vérifier le numéro de TVA', revalidate: 'Vérifier à nouveau' },
  states: {
    loading: 'Chargement de l’historique',
    loadError: 'Impossible de charger l’historique',
    executeError: 'La validation n’a pas pu être effectuée.',
    empty: 'Aucune validation VIES enregistrée.',
    noVatTitle: 'Aucun numéro de TVA enregistré',
    noVat: 'Ajoutez d’abord un numéro de TVA syntaxiquement valide à la fiche.',
  },
  result: {
    VALID: 'Le numéro de TVA est confirmé valide.',
    INVALID: 'Le numéro de TVA est confirmé invalide.',
    NOT_CHECKABLE: 'Le numéro de TVA n’a pas pu être vérifié.',
    SERVICE_UNAVAILABLE: 'VIES est indisponible ; le numéro n’a pas été marqué invalide.',
  },
  deviation: {
    title: 'Écart avec les données de base',
    description: 'Vérifiez le nom et l’adresse avant toute utilisation fiscale.',
  },
  settings: {
    action: 'Paramètres',
    title: 'Validation périodique',
    description: 'Gère le cache, les nouvelles tentatives et les contrôles VIES périodiques.',
    automatic: 'Vérifier automatiquement selon la période',
    intervalDays: 'Intervalle de contrôle (jours)',
    cacheHours: 'Cache (heures)',
    retryHours: 'Nouvel essai après panne (heures)',
    saved: 'Paramètres du numéro de TVA enregistrés.',
  },
} as const;

export const vatValidationEs = {
  title: 'Validación del NIF-IVA',
  subtitle: 'Confirmación cualificada mediante el servicio oficial VIES de la UE.',
  help: 'Ayuda sobre la validación VIES',
  history: 'Historial de validaciones',
  source: 'Fuente',
  nameMatch: 'Nombre',
  addressMatch: 'Dirección',
  requestIdentifier: 'Identificador de solicitud',
  returnedName: 'Nombre devuelto por VIES',
  returnedAddress: 'Dirección devuelta por VIES',
  countryAndNumber: 'País y número comprobados',
  providerDate: 'Fecha de comprobación VIES',
  nameAssessment: 'Comparación del nombre',
  notProvided: 'No se proporcionó ningún nombre',
  rawValueNotice: 'Valor sin modificar, tras decodificar XML, de la respuesta VIES.',
  normalizedMasterName: 'Datos maestros — vista normalizada',
  normalizedReportedName: 'VIES — vista normalizada',
  normalizedNotice:
    'La normalización solo sirve para comparar. Las partes resaltadas faltan en la otra vista.',
  disclaimer:
    'La comprobación técnica de VIES es una prueba en la fecha de consulta, pero no una garantía fiscal automática.',
  status: {
    UNCHECKED: 'Sin comprobar',
    VALID: 'Válido',
    INVALID: 'No válido',
    NOT_CHECKABLE: 'No comprobable',
    SERVICE_UNAVAILABLE: 'Servicio no disponible',
  },
  match: { MATCH: 'Coincide', NO_MATCH: 'No coincide', NOT_PROCESSED: 'No procesado' },
  nameState: {
    NOT_APPLICABLE: 'No aplicable',
    NOT_PROVIDED: 'Nombre no proporcionado',
    LIKELY_MATCH: 'El nombre probablemente coincide',
    MISMATCH: 'El nombre difiere',
    MANUAL_REVIEW: 'Revisión manual necesaria',
  },
  legacy: {
    title: 'Vuelva a comprobar este resultado antiguo',
    description:
      'Esta prueba usa el mapeo VIES anterior. Se conserva intacta; una nueva comprobación crea una prueba correctamente asignada.',
  },
  actions: { validate: 'Comprobar NIF-IVA', revalidate: 'Comprobar de nuevo' },
  states: {
    loading: 'Cargando historial',
    loadError: 'No se pudo cargar el historial',
    executeError: 'No se pudo realizar la validación.',
    empty: 'Todavía no hay ninguna validación VIES.',
    noVatTitle: 'No hay NIF-IVA registrado',
    noVat: 'Añada primero un NIF-IVA sintácticamente válido a la ficha.',
  },
  result: {
    VALID: 'El NIF-IVA se ha confirmado como válido.',
    INVALID: 'El NIF-IVA se ha confirmado como no válido.',
    NOT_CHECKABLE: 'No se pudo comprobar el NIF-IVA.',
    SERVICE_UNAVAILABLE: 'VIES no está disponible; el NIF-IVA no se marcó como no válido.',
  },
  deviation: {
    title: 'Diferencia con los datos maestros',
    description: 'Revise el nombre y la dirección antes de usar el resultado fiscalmente.',
  },
  settings: {
    action: 'Configuración',
    title: 'Validación periódica',
    description: 'Controla la caché, los reintentos y las comprobaciones VIES periódicas.',
    automatic: 'Comprobar automáticamente de forma periódica',
    intervalDays: 'Intervalo de comprobación (días)',
    cacheHours: 'Caché (horas)',
    retryHours: 'Reintento tras fallo (horas)',
    saved: 'Configuración del NIF-IVA guardada.',
  },
} as const;

export const vatValidationRu = {
  title: 'Проверка номера НДС',
  subtitle: 'Квалифицированное подтверждение через официальную службу EU VIES.',
  help: 'Справка по проверке VIES',
  history: 'История проверок',
  source: 'Источник',
  nameMatch: 'Название',
  addressMatch: 'Адрес',
  requestIdentifier: 'Идентификатор запроса',
  returnedName: 'Название из VIES',
  returnedAddress: 'Адрес из VIES',
  countryAndNumber: 'Проверенные страна и номер',
  providerDate: 'Дата проверки VIES',
  nameAssessment: 'Сравнение названия',
  notProvided: 'Название не предоставлено',
  rawValueNotice: 'Неизменённое значение из ответа VIES после декодирования XML.',
  normalizedMasterName: 'Основные данные — нормализованное сравнение',
  normalizedReportedName: 'VIES — нормализованное сравнение',
  normalizedNotice:
    'Нормализация используется только для сравнения. Выделенные части отсутствуют в другом варианте.',
  disclaimer:
    'Техническая проверка VIES является свидетельством на момент запроса, но не автоматической налоговой гарантией.',
  status: {
    UNCHECKED: 'Не проверен',
    VALID: 'Действителен',
    INVALID: 'Недействителен',
    NOT_CHECKABLE: 'Нельзя проверить',
    SERVICE_UNAVAILABLE: 'Служба недоступна',
  },
  match: { MATCH: 'Совпадает', NO_MATCH: 'Не совпадает', NOT_PROCESSED: 'Не обработано' },
  nameState: {
    NOT_APPLICABLE: 'Неприменимо',
    NOT_PROVIDED: 'Название не предоставлено',
    LIKELY_MATCH: 'Название, вероятно, совпадает',
    MISMATCH: 'Название отличается',
    MANUAL_REVIEW: 'Требуется ручная проверка',
  },
  legacy: {
    title: 'Проверьте старый результат повторно',
    description:
      'Это свидетельство использует прежнее сопоставление VIES. Оно остаётся неизменным; повторная проверка создаст корректно сопоставленное свидетельство.',
  },
  actions: { validate: 'Проверить номер НДС', revalidate: 'Проверить повторно' },
  states: {
    loading: 'Загрузка истории',
    loadError: 'Не удалось загрузить историю',
    executeError: 'Не удалось выполнить проверку.',
    empty: 'Проверок VIES пока нет.',
    noVatTitle: 'Номер НДС не указан',
    noVat: 'Сначала добавьте в карточку синтаксически корректный номер НДС.',
  },
  result: {
    VALID: 'Номер НДС подтверждён как действительный.',
    INVALID: 'Номер НДС подтверждён как недействительный.',
    NOT_CHECKABLE: 'Номер НДС не удалось проверить.',
    SERVICE_UNAVAILABLE: 'VIES недоступен; номер не отмечен как недействительный.',
  },
  deviation: {
    title: 'Отличие от основных данных',
    description: 'Проверьте название и адрес перед использованием в налоговых целях.',
  },
  settings: {
    action: 'Настройки',
    title: 'Периодическая проверка',
    description: 'Управляет кэшем, повторами и периодическими проверками VIES.',
    automatic: 'Автоматически проверять по расписанию',
    intervalDays: 'Интервал (дни)',
    cacheHours: 'Кэш (часы)',
    retryHours: 'Повтор после сбоя (часы)',
    saved: 'Настройки номера НДС сохранены.',
  },
} as const;
